import type { FC } from "react";
import { CheckboxInputField } from "@/components/flexkit/checkbox/CheckboxInputField";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import type { PropsWithClassName } from "@/components/podkit/lib/cn";
import type { WorkflowEditFormStepData } from "@/components/automations/automation-edit-form-data";
import { useOnBlurError } from "@/hooks/use-onblur-error";
import { z } from "zod";

const titleSchema = z.string().min(1, "Title is required");
const branchSchema = z.string().min(1, "Branch name is required");

type PullRequestStep = Extract<WorkflowEditFormStepData, { type: "pr" }>;

type AutomationPullRequestStepEditorProps = {
    step: PullRequestStep;
    onChange: (updates: Partial<PullRequestStep>) => void;
};

export const AutomationPullRequestStepEditor: FC<AutomationPullRequestStepEditorProps> = ({ step, onChange }) => {
    const { message: titleError, onBlur: onBlurTitle } = useOnBlurError(titleSchema, step.title);
    const { message: branchError, onBlur: onBlurBranch } = useOnBlurError(branchSchema, step.branch);

    return (
        <div className="flex flex-col gap-4 px-6">
            <TitleInputField
                value={step.title}
                onChange={(title) => onChange({ title })}
                error={titleError}
                onBlur={onBlurTitle}
            />
            <DescriptionInputField value={step.description} onChange={(description) => onChange({ description })} />
            <BranchInputField
                value={step.branch}
                onChange={(branch) => onChange({ branch })}
                error={branchError}
                onBlur={onBlurBranch}
                className="flex-1"
            />
            <DraftCheckboxInputField checked={step.isDraft} onChange={(isDraft) => onChange({ isDraft })} />
        </div>
    );
};

const TitleInputField: FC<{
    value: string;
    onChange: (value: string) => void;
    error?: string;
    onBlur?: () => void;
}> = ({ value, onChange, error, onBlur }) => {
    return (
        <InputField label="Title" hint="Template for generating a PR title" error={error}>
            <Input
                className="w-full max-w-full"
                type="text"
                value={value}
                placeholder="chore: <short-description>"
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
            />
        </InputField>
    );
};

const DescriptionInputField: FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
    return (
        <InputField label="Description (optional)" hint="Template for generating the PR description">
            <Textarea
                value={value}
                placeholder=""
                onChange={(e) => onChange(e.target.value)}
                className="resize-none"
                minHeight={150}
                maxHeight={150}
            />
        </InputField>
    );
};

const BranchInputField: FC<
    PropsWithClassName<{ value: string; onChange: (value: string) => void; error?: string; onBlur?: () => void }>
> = ({ value, onChange, error, onBlur, className }) => {
    return (
        <InputField label="Branch" hint="Template for generating a branch name" className={className} error={error}>
            <Input
                className="w-full max-w-full"
                type="text"
                value={value}
                placeholder="chore/<short-description>"
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
            />
        </InputField>
    );
};

const DraftCheckboxInputField: FC<{ checked: boolean; onChange: (checked: boolean) => void }> = ({
    checked,
    onChange,
}) => {
    return <CheckboxInputField label="Open PR as draft" checked={checked} onChange={onChange} />;
};
